import { Icon } from "@opendash/icons";
import {
  Descriptions,
  Divider,
  Drawer,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { ExportButton } from "../../../components/ExportButton";
import { ImageGallery } from "../../../components/ImageGallery";
import {
  SIV_Package,
  SIV_PackageItem,
  SIV_PackageText,
} from "../../../parsegenerated";
import { usePackageItems, usePackageTexts } from "../hooks/usePackages";

type Props = {
  package: SIV_Package | null;
  open: boolean;
  onClose: () => void;
};

export const PackageDetailDrawer = observer((props: Props) => {
  const { package: pkg, open, onClose } = props;
  const { items, loading } = usePackageItems(pkg);
  const { texts, loading: textsLoading } = usePackageTexts(pkg);
  const imageDirectory =
    (pkg?.get("directory") as string | undefined) || undefined;

  const textColumns: ColumnType<SIV_PackageText>[] = [
    {
      title: "Nr.",
      dataIndex: "no",
      key: "no",
      width: 60,
    },
    {
      title: "Text",
      dataIndex: "text",
      key: "text",
      render: (val: string) => val || "-",
    },
  ];

  const itemColumns: ColumnType<SIV_PackageItem>[] = [
    {
      title: "WE-Nr.",
      dataIndex: "goodsReceiptNo",
      key: "goodsReceiptNo",
      width: 100,
    },
    {
      title: "WE-Pos.",
      dataIndex: "goodsReceiptPosNo",
      key: "goodsReceiptPosNo",
      width: 80,
    },
    {
      title: "Verpackte Menge",
      dataIndex: "packedQuantity",
      key: "packedQuantity",
      width: 120,
      render: (val: number) => val ?? "-",
    },
    {
      title: "Verpacktes Gewicht",
      dataIndex: "packedWeight",
      key: "packedWeight",
      width: 130,
      render: (val: number) => (val ? `${val} kg` : "-"),
    },
  ];

  // Prepare export data
  const exportData = [
    {
      Feld: "Nummer",
      Wert: pkg?.no || "",
    },
    {
      Feld: "Kunden-Nr.",
      Wert: pkg?.customerPackageNo || "",
    },
    {
      Feld: "Packstück-Nr.",
      Wert: pkg?.packageNo || "",
    },
    {
      Feld: "Verpackungsdatum",
      Wert: pkg?.packagingDate
        ? dayjs(pkg.packagingDate).format("DD.MM.YYYY")
        : "",
    },
    {
      Feld: "Verpackungsart",
      Wert: pkg?.packagingType || "",
    },
    {
      Feld: "Länge (mm)",
      Wert: pkg?.packageLength ?? "",
    },
    {
      Feld: "Breite (mm)",
      Wert: pkg?.packageWidth ?? "",
    },
    {
      Feld: "Höhe (mm)",
      Wert: pkg?.packageHeight ?? "",
    },
    {
      Feld: "Netto-Gewicht (kg)",
      Wert: pkg?.netWeight ?? "",
    },
    {
      Feld: "Brutto-Gewicht (kg)",
      Wert: pkg?.grossWeight ?? "",
    },
    {
      Feld: "Volumen (m³)",
      Wert: pkg?.volume ?? "",
    },
    {
      Feld: "Oberfläche (m²)",
      Wert: pkg?.surface ?? "",
    },
    {
      Feld: "Transport-Code",
      Wert: pkg?.transportCode || "",
    },
    {
      Feld: "Status Ablauf",
      Wert: pkg?.statusProcess || "",
    },
    {
      Feld: "Status Fertigmeldung",
      Wert: pkg?.statusCompletion || "",
    },
    {
      Feld: "Status Rechnung",
      Wert: pkg?.statusInvoice || "",
    },
  ];

  return (
    <Drawer
      title={
        <>
          <Icon icon="fa:box" /> Packstück {pkg?.no}
        </>
      }
      open={open}
      onClose={onClose}
      width="min(800px, 100vw)"
      extra={
        <Space>
          <ExportButton
            data={exportData}
            filename={`Packstück_${pkg?.no || "export"}`}
          />
        </Space>
      }
    >
      {pkg && (
        <>
          <Descriptions
            column={2}
            bordered
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Nummer">{pkg.no}</Descriptions.Item>
            <Descriptions.Item label="Kunden-Nr.">
              {pkg.customerPackageNo || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Packstück-Nr.">
              {pkg.packageNo || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Verpackungsdatum">
              {pkg.packagingDate
                ? dayjs(pkg.packagingDate).format("DD.MM.YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Verpackungsart">
              {pkg.packagingType ? (
                <Tag color="purple">{pkg.packagingType}</Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Bauart">
              {pkg.constructionType || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Länge">
              {pkg.packageLength ? `${pkg.packageLength} mm` : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Breite">
              {pkg.packageWidth ? `${pkg.packageWidth} mm` : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Höhe">
              {pkg.packageHeight ? `${pkg.packageHeight} mm` : "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Netto-Gewicht">
              {pkg.netWeight ? `${pkg.netWeight} kg` : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Brutto-Gewicht">
              {pkg.grossWeight ? `${pkg.grossWeight} kg` : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Volumen">
              {pkg.volume ? `${pkg.volume} m³` : "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Oberfläche">
              {pkg.surface ? `${pkg.surface} m²` : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Transport-Code">
              {pkg.transportCode || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Lagervorschrift">
              {pkg.storageInstruction || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Status Ablauf">
              {pkg.statusProcess ? (
                <Tag color="blue">{pkg.statusProcess}</Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Status Fertigmeldung">
              {pkg.statusCompletion ? (
                <Tag color="green">{pkg.statusCompletion}</Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Status Rechnung">
              {pkg.statusInvoice ? (
                <Tag color="orange">{pkg.statusInvoice}</Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>

            <Descriptions.Item label="Fertigungsende">
              {pkg.productionEndDate
                ? dayjs(pkg.productionEndDate).format("DD.MM.YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Fertigungsbetrieb">
              {pkg.productionFacility || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="SIV Betrieb">
              {pkg.sivFacility || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Verpackungsende">
              {pkg.packagingEndDate
                ? dayjs(pkg.packagingEndDate).format("DD.MM.YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Verpackt durch">
              {pkg.packedBy || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Verpackungsort">
              {pkg.packagingLocation || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Kurzbeschreibung" span={2}>
              <span style={{ wordBreak: "break-word" }}>
                {pkg.shortDescription || "-"}
              </span>
            </Descriptions.Item>
          </Descriptions>

          <ImageGallery
            directory={imageDirectory}
            title="Bilder"
            height={170}
          />

          <Divider />

          <Typography.Title level={5} style={{ marginBottom: 12 }}>
            <Icon icon="fa:align-left" /> Texte
          </Typography.Title>

          {textsLoading ? (
            <Spin />
          ) : (
            <Table
              columns={textColumns}
              dataSource={texts}
              rowKey={(record) => record.id!}
              size="small"
              pagination={false}
            />
          )}

          <Divider />

          <Typography.Title level={5} style={{ marginBottom: 12 }}>
            <Icon icon="fa:list" /> Inhalte
          </Typography.Title>

          {loading ? (
            <Spin />
          ) : (
            <Table
              columns={itemColumns}
              dataSource={items}
              rowKey={(record) => record.id!}
              size="small"
              pagination={false}
            />
          )}
        </>
      )}
    </Drawer>
  );
});
